import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useParams } from "react-router";
import type {
  OrganizationsBrandsLookupBrandItem,
  OrganizationsBrandsLookupItem,
} from "@src/domain/dto/organizationsBrands";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { ConfigGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/ConfigGraphQlAdapter";
import { clearConfigListCache } from "@src/presentation/providers/config/configListCache";
import { findOrgBrandByKeys } from "./utils/findOrgBrandByKeys";
import { isConfigSection, type ConfigSection } from "./configNav.config";

export interface ConfigBrandContextValue {
  organizationKey: string | null;
  brandKey: string | null;
  section: ConfigSection | null;
  organization: OrganizationsBrandsLookupItem | null;
  brand: OrganizationsBrandsLookupBrandItem | null;
  isBrandSelected: boolean;
  isLookupLoading: boolean;
  isLookupReady: boolean;
  isInvalidBrandSelection: boolean;
}

const ConfigBrandContext = createContext<ConfigBrandContextValue | null>(null);

interface ConfigBrandProviderProps {
  children: ReactNode;
}

export const ConfigBrandProvider = ({ children }: ConfigBrandProviderProps) => {
  const { organizationKey, brandKey, section } = useParams<{
    organizationKey?: string;
    brandKey?: string;
    section?: string;
  }>();
  const { organizationsBrandsLookup, organizationsBrandsLookupLoading } =
    useGlobalDataLookup();

  // Drop list cache when leaving Config entirely.
  useEffect(() => {
    return () => {
      clearConfigListCache();
    };
  }, []);

  useEffect(() => {
    const key = brandKey?.trim() || null;
    ConfigGraphQlAdapter.getInstance().setBrandKey(key);
    return () => {
      ConfigGraphQlAdapter.getInstance().setBrandKey(null);
    };
  }, [brandKey]);

  const value = useMemo<ConfigBrandContextValue>(() => {
    const orgKey = organizationKey ?? null;
    const brKey = brandKey ?? null;
    const resolvedSection = isConfigSection(section) ? section : null;
    const isLookupReady =
      !organizationsBrandsLookupLoading && organizationsBrandsLookup != null;

    if (!orgKey || !brKey) {
      return {
        organizationKey: orgKey,
        brandKey: brKey,
        section: resolvedSection,
        organization: null,
        brand: null,
        isBrandSelected: false,
        isLookupLoading: organizationsBrandsLookupLoading,
        isLookupReady,
        isInvalidBrandSelection: false,
      };
    }

    const match =
      organizationsBrandsLookup == null
        ? null
        : findOrgBrandByKeys(organizationsBrandsLookup, orgKey, brKey);

    return {
      organizationKey: orgKey,
      brandKey: brKey,
      section: resolvedSection,
      organization: match?.organization ?? null,
      brand: match?.brand ?? null,
      isBrandSelected: Boolean(match),
      isLookupLoading: organizationsBrandsLookupLoading,
      isLookupReady,
      isInvalidBrandSelection: isLookupReady && !match,
    };
  }, [
    organizationKey,
    brandKey,
    section,
    organizationsBrandsLookup,
    organizationsBrandsLookupLoading,
  ]);

  return (
    <ConfigBrandContext.Provider value={value}>
      {children}
    </ConfigBrandContext.Provider>
  );
};

export function useConfigBrand(): ConfigBrandContextValue {
  const context = useContext(ConfigBrandContext);
  if (!context) {
    throw new Error("useConfigBrand must be used within ConfigBrandProvider");
  }
  return context;
}
